import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QrCode, Printer, Download, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EquipmentQRDialog({ open, onClose, equipment }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [pageUrl, setPageUrl] = useState("");

  useEffect(() => {
    if (open && equipment) {
      const token = ""; // httpOnly cookie auth
      setImgSrc(`${API}/equipment/${equipment.id}/qr?auth=${encodeURIComponent(token)}&t=${Date.now()}`);
      setPageUrl(`${window.location.origin}/equipment/${equipment.id}`);
    }
  }, [open, equipment]);

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(pageUrl); toast.success("URL copiée"); }
    catch { toast.error("Copie impossible"); }
  };

  const print = () => {
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) return;
    // Safer than document.write: build DOM nodes explicitly (avoids XSS via templated strings)
    const d = w.document;
    d.title = `QR ${equipment?.name || ""}`;
    d.body.style.fontFamily = "sans-serif";
    d.body.style.textAlign = "center";
    d.body.style.padding = "40px";

    const h1 = d.createElement("h1");
    h1.style.margin = "0 0 8px";
    h1.textContent = equipment?.name || "";
    d.body.appendChild(h1);

    const meta = d.createElement("div");
    meta.style.color = "#666";
    meta.style.marginBottom = "24px";
    meta.style.fontSize = "14px";
    meta.textContent = [equipment?.brand, equipment?.plate].filter(Boolean).join(" · ");
    d.body.appendChild(meta);

    const img = d.createElement("img");
    img.src = imgSrc;
    img.style.width = "280px";
    img.style.height = "280px";
    img.style.imageRendering = "pixelated";
    d.body.appendChild(img);

    const url = d.createElement("div");
    url.style.marginTop = "16px";
    url.style.color = "#999";
    url.style.fontSize = "11px";
    url.style.wordBreak = "break-all";
    url.textContent = pageUrl;
    d.body.appendChild(url);

    const brand = d.createElement("div");
    brand.style.marginTop = "24px";
    brand.style.color = "#000";
    brand.style.fontSize = "10px";
    brand.style.letterSpacing = "2px";
    brand.textContent = "BKFLOW · SCAN POUR HISTORIQUE";
    d.body.appendChild(brand);

    // Trigger print once the image is loaded
    if (img.complete) setTimeout(() => w.print(), 300);
    else img.onload = () => setTimeout(() => w.print(), 300);
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = imgSrc;
    a.download = `qr-${equipment?.name?.replace(/\s+/g, "-") || equipment?.id}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (!equipment) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode size={20} weight="duotone" className="text-[#005CFF]" />
            QR — {equipment.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-2xl bg-white p-4 flex items-center justify-center">
            {imgSrc && <img src={imgSrc} alt="QR code" className="w-full max-w-[240px]" data-testid="equipment-qr-image" />}
          </div>
          <div className="text-[11px] text-white/50 text-center">
            Scannez ce QR sur le terrain pour ouvrir l&apos;historique de l&apos;équipement dans BKflow.
          </div>
          <div className="flex items-center gap-1 text-[10px] text-white/40 bg-white/5 rounded-lg p-2 break-all font-mono">
            {pageUrl}
            <button onClick={copyUrl} className="ml-auto shrink-0 text-white/60 hover:text-white" title="Copier"><Copy size={12} /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={print} variant="ghost" className="border border-white/10 h-10" data-testid="equipment-qr-print"><Printer size={14} /> Imprimer</Button>
            <Button onClick={download} className="bg-[#005CFF] hover:bg-[#0047CC] h-10" data-testid="equipment-qr-download"><Download size={14} /> Télécharger</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
